import React from 'react';
import { PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import ReleaseNoteBlocks from '../../release-notes/components/ReleaseNoteBlocks';
import { UserAvatar } from '../../../components/ui';
import { formatPublishedAt, publisherName } from '../../release-notes/release-note-model';
import { needsUpload } from '../release-note-editor-model';
import '../../release-notes/release-notes.css';

// What readers will see, drawn from the unsaved form (spec §7.3). Media blocks
// that have no file yet get a placeholder; everything else goes through the
// same renderer as the detail page.
const EditorPreview = ({ form, note }) => {
  // A blank field falls back to the saved name: the API keeps it on a blank save.
  const publisher = form.publishername.trim() || publisherName(note);
  return (
    <section aria-label="Preview" className="flex flex-col gap-2.5 xl:sticky xl:top-[calc(8vh+1.5rem)]">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Preview</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">What readers will see</span>
      </div>
      <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900/40">
        <span className="text-[0.65rem] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {note?.status === 10 && note?.publishedat ? `Published ${formatPublishedAt(note.publishedat)}` : 'Not published yet'}
        </span>
        <h2 className="rn-display text-2xl font-semibold leading-tight tracking-tight text-slate-900 dark:text-slate-100">
          {form.title.trim() || 'Untitled note'}
        </h2>
        {form.tags.length ? (
          <div className="flex flex-wrap gap-1">
            {form.tags.map((t) => (
              <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">{t}</span>
            ))}
          </div>
        ) : null}
        {publisher ? (
          <div data-testid="rn-preview-publisher" className="flex min-w-0 items-center gap-3.5">
            {/* The photo is the note's (server state); the name may be the typed one. */}
            <span aria-hidden="true"><UserAvatar name={publisher} src={note?.publisherphotourl || undefined} size="xl" /></span>
            <span className="min-w-0 break-words text-base font-semibold text-slate-900 dark:text-slate-100">{publisher}</span>
          </div>
        ) : null}
        {form.summary.trim() ? <p className="text-base text-slate-900 dark:text-slate-100">{form.summary}</p> : null}
        {form.blocks.map((block) =>
          needsUpload(block) ? (
            <div key={block.key} className="flex aspect-video items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
              {block.type === 'image' ? <PhotoIcon className="h-5 w-5" aria-hidden /> : <VideoCameraIcon className="h-5 w-5" aria-hidden />}
              {block.type === 'image' ? 'Image not uploaded yet' : 'Video not uploaded yet'}
            </div>
          ) : (
            <ReleaseNoteBlocks key={block.key} blocks={[block]} />
          ),
        )}
      </div>
    </section>
  );
};

export default EditorPreview;
